/* eslint-disable max-lines */
import { NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import type { ElementRef } from '@angular/core';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { select, Store } from '@ngrx/store';
import { deepEqual } from 'fast-equals';
import type { Observable } from 'rxjs';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime, filter, startWith, tap } from 'rxjs/operators';

// eslint-disable-next-line boundaries/element-types -- FIXME
import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { AccountSelectors, UserSelectors } from '@core/account-data';
import { AdminFilterPipe } from '@core/account-logic';
import type { User } from '@core/account-types';
import { TeamMembersAnalytics } from '@core/analytics-legacy';
import { filterBoolean } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { InvitationActions, InvitationSelectors } from '@core/invitations-data';
import type { Invitation } from '@core/invitations-types';
import type { TOfferKind } from '@core/offers-types';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { RoleSelectors } from '@core/roles-data';
import type { Role } from '@core/roles-types';
import type { DialogComponentI, DialogData, SelectOption } from '@core/user-interface-legacy';
import {
  AvatarComponent,
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  EmptyStateComponent,
  FormFieldComponent,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  NotificationsController,
  RtTooltip,
  TruncateComponent,
} from '@core/user-interface-legacy';
import { includesWithTrim } from '@core/utils-legacy';
import type { RoleWithAdmins } from '@core/visibility-legacy';
import { InvitationFormComponent, TeamMembersItemComponent } from '@recruitee/memberships-ui';

type UserToDisplay = User & Partial<Assignment> & { isDisabled?: boolean };

type Assignment = {
  id?: number;
  adminId: number;
  following?: boolean;
  isHiringManager?: boolean;
  isRecruiter?: boolean;
  isApprover?: boolean;
};

type DialogData = {
  context: TOfferKind | 'requisition';
  assignments: Assignment[];
  allowInvite?: boolean;
  submitHandler$?: (
    invitations: Array<{ email: string; roleId: number }>,
    adminIds: number[],
  ) => Observable<boolean>;
};

@Component({
  selector: 'rt-team-members-dialog',
  templateUrl: 'team-members-dialog.component.html',
  styleUrls: ['./team-members-dialog.component.less'],
  imports: [
    AvatarComponent,
    BadgeComponent,
    BoxComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    CheckboxComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DividerComponent,
    EmptyStateComponent,
    FormFieldComponent,
    GroupComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    InputComponent,
    InvitationFormComponent,
    RtTooltip,
    TeamMembersItemComponent,
    TruncateComponent,
    VirtualScrollerModule,
    TranslatePipe,
    ReactiveFormsModule,
    NgTemplateOutlet,
    UpperCasePipe,
  ],
  viewProviders: [TeamMembersAnalytics, AdminFilterPipe],
})
export class TeamMembersDialogComponent implements DialogComponentI<DialogData, boolean> {
  private readonly dialog = inject(DialogRef);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly notificationsCtrl = inject(NotificationsController);
  private readonly i18n = inject(I18nService);
  private readonly analytics = inject(TeamMembersAnalytics);
  private readonly adminFilterPipe = inject(AdminFilterPipe);

  protected readonly cdRef = inject(ChangeDetectorRef);

  public loading: boolean = false;
  public allowedAdmins: UserToDisplay[] = [];
  public disallowedAdmins: UserToDisplay[] = [];
  public roles: SelectOption[] = [];
  public rolesWithAdmins: RoleWithAdmins[] = [];
  public assignments: Assignment[] = [];
  public memberCount: number = 0;
  public searchQuery: string = '';
  public invitationFormOpened: boolean = false;
  public invitationFormValid: boolean = false;
  public invitations: Array<{ email: string; roleId: number }> = [];
  public searchControl = new FormControl<string>('', { nonNullable: true });
  public assignmentsControl: FormControl<number[]> = new FormControl([], { nonNullable: true });
  public canManageRecruiters = false;
  public currentAdminEmails: string[] = [];
  public invitedEmails: string[] = [];

  public virtualScrollUsers: UserToDisplay[] = [];
  public virtualScrollDisabledUsers: UserToDisplay[] = [];
  public virtualScrollItems: UserToDisplay[] = [];

  public filteredUsers: UserToDisplay[] = this.allowedAdmins;
  public filteredDisallowedUsers: UserToDisplay[] = this.disallowedAdmins;

  public translations = {
    offer: {
      badgesTooltip: this.i18n.translate('app.offer_assignments.role_badge_tooltip'),
      disabledCheckboxTooltip: this.i18n.translate(
        'app.offer_assignments.remove_member_disabled_tooltip',
      ),
      disabledUserTooltip: this.i18n.translate(
        'app.offer_assignments.offer_team_members_dialog.disabled_user_tooltip',
      ),
      subheading: (memberCount: number) =>
        this.i18n.translate('app.offer_assignments.offer_team_members_dialog.subheading_plural', {
          count: memberCount,
        }),
      groupItemDescription: (count: number) =>
        this.i18n.translate('app.offer_assignments.can_access_all_jobs_plural', { count }),
    },
    requisition: {
      badgesTooltip: this.i18n.translate('app.requisitions.team_members.badges_tooltip'),
      disabledCheckboxTooltip: this.i18n.translate(
        'app.requisitions.team_members.cant_remove_tooltip',
      ),
      subheading: (memberCount: number) =>
        this.i18n.translate('app.requisitions.team_members.team_members_dialog.subheading', {
          count: memberCount,
        }),
      groupItemDescription: (count: number) =>
        this.i18n.translate('app.requisitions.team_members.can_access_description', { count }),
    },
  };
  public allowInvite: boolean;
  public context: TOfferKind | 'requisition';

  private isAdmin: boolean;
  private requiredAbilityForGroupedAdmins: Ability;
  private submitHandler$:
    | ((
        invitations: Array<{ email: string; roleId: number }>,
        adminIds: number[],
      ) => Observable<boolean>)
    | null;

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(currentCompany => (this.isAdmin = currentCompany.isAdmin));
  }

  public ngOnInit(): void {
    this.store
      .select(UserSelectors.entities())
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(admins => {
        const users = admins.map(admin => {
          const assignment = this.assignments.find(assignment => assignment.adminId === admin.id);
          if (assignment) {
            const { isRecruiter, isHiringManager, isApprover } = assignment;
            return {
              ...admin,
              isRecruiter,
              isHiringManager,
              isApprover,
              isDisabled: isRecruiter || isHiringManager || isApprover,
            };
          }
          return admin;
        });
        this.allowedAdmins = this.getAllowedAdmins(users);
        this.disallowedAdmins = this.getDisallowedAdmins(users);
        this.memberCount = this.getAdminsCount(
          admins,
          this.assignments.map(assignment => assignment.adminId),
        );
        this.rolesWithAdmins = this.filterAndGroupAdmins(admins);
        this.currentAdminEmails = admins.map(admin => admin.email.toLowerCase());
        this.cdRef.detectChanges();
      });

    this.store
      .select(RoleSelectors.entities())
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe((roles: Role[]) => {
        this.roles = roles
          .filter(role => (this.isAdmin ? true : !role.admin))
          .map(role => ({
            value: role.id,
            label: role.name,
          }));
        this.cdRef.detectChanges();
      });

    this.assignmentsControl.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(assignments => {
        this.memberCount = this.getAdminsCount(this.allowedAdmins, assignments);
        this.cdRef.detectChanges();
      });

    this.store
      .pipe(
        select(AbilitiesSelectors.hasOne(Ability.ManageRecruiters)),
        tap(enabled => (this.canManageRecruiters = enabled)),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(() => this.loadInvitations());

    this.searchControl.valueChanges
      .pipe(debounceTime(100), startWith(''), takeUntil(this.ngOnDestroy$))
      .subscribe((searchControlValue: string) => {
        this.searchQuery = searchControlValue;
        this.filteredUsers = this.adminFilterPipe.transform(this.allowedAdmins, searchControlValue);
        this.filteredDisallowedUsers = this.adminFilterPipe.transform(
          this.disallowedAdmins,
          searchControlValue,
        );
        this.virtualScrollItems = this.filteredUsers.concat(this.filteredDisallowedUsers);
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public get badgesTooltipText(): string {
    return this.context === 'requisition'
      ? this.translations.requisition.badgesTooltip
      : this.translations.offer.badgesTooltip;
  }

  public get disabledCheckboxTooltipText(): string {
    return this.context === 'requisition'
      ? this.translations.requisition.disabledCheckboxTooltip
      : this.translations.offer.disabledCheckboxTooltip;
  }

  public get subheading(): string {
    return this.context === 'requisition'
      ? this.translations.requisition.subheading(this.memberCount)
      : this.translations.offer.subheading(this.memberCount);
  }

  public groupItemDescription(count: number): string {
    return this.context === 'requisition'
      ? this.translations.requisition.groupItemDescription(count)
      : this.translations.offer.groupItemDescription(count);
  }

  public setDialogData(data: DialogData): void {
    this.allowInvite = data.allowInvite ?? true;
    this.submitHandler$ = data.submitHandler$ ?? null;
    this.context = data.context;
    this.requiredAbilityForGroupedAdmins =
      this.context === 'requisition' ? Ability.ViewAllRequisitions : Ability.ViewAllJobs;
    this.assignments = data.assignments;
    this.assignmentsControl.setValue(data.assignments.map(assignment => assignment.adminId));
    this.cdRef.detectChanges();
  }

  public toggleAssignment(admin: UserToDisplay): void {
    if (!admin?.isDisabled) {
      let assignments = [...this.assignmentsControl.value];
      if (assignments.includes(admin.id)) {
        assignments = assignments.filter(adminId => adminId !== admin.id);
      } else {
        assignments.push(admin.id);
      }
      this.assignmentsControl.setValue(assignments);
      this.assignmentsControl.markAsDirty();
      this.cdRef.detectChanges();
    }
  }

  public toggleInvitationForm(): void {
    this.invitationFormOpened = !this.invitationFormOpened;
    this.invitations = [];
    this.invitationFormValid = false;
    this.cdRef.detectChanges();
  }

  public isSearching(): boolean {
    return !!this.searchControl.value && this.searchControl.value.length > 0;
  }

  public cancelSearch(): void {
    this.searchControl.reset('');
    this.onSearchQueryChange('');
  }

  public cancel(): void {
    this.dialog.close();
  }

  public submit(): void {
    const adminIds = this.assignmentsControl.value;
    if (!this.submitHandler$) {
      this.dialog.close(adminIds);
      return;
    }

    this.loading = true;
    this.cdRef.detectChanges();
    this.submitHandler$(this.invitations, adminIds)
      .pipe(
        tap(() => {
          if (!deepEqual(adminIds, this.assignments)) {
            this.analytics.trackMemberAccess(this.context);
          }
          if (this.invitations.length) {
            this.analytics.trackInviteMembers();
          }
        }),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(success => {
        if (success) {
          this.notificationsCtrl.success(
            this.i18n.translate('app.offer_assignments.offer_team_members_dialog.changed'),
          );

          this.loading = false;
          this.dialog.close();
        }
      });
  }

  public onSearchQueryChange(query: string): void {
    this.searchQuery = query;
    this.rolesWithAdmins = this.filterAndGroupAdmins(this.allowedAdmins, query);
    this.cdRef.detectChanges();
  }

  public goToManageRole(roleId: number): void {
    this.router.navigate(['/settings/roles', roleId]).then(() => {
      this.dialog.close();
    });
  }

  public scrollToTeamMembersItem({ elRef }: { elRef: ElementRef }) {
    elRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  public trackById<T extends { id: number }>(index: number, { id }: T): number {
    return id;
  }

  public onValueChanges({
    invitations,
    valid,
  }: {
    valid: boolean;
    invitations: Array<{ email: string; roleId: number }>;
  }): void {
    this.invitations = invitations;
    this.invitationFormValid = valid;
    this.cdRef.detectChanges();
  }

  public isFormValid(): boolean {
    return (
      (this.invitationFormOpened && !this.invitationFormValid) ||
      (!this.invitationFormOpened && this.assignmentsControl.pristine)
    );
  }

  public onVirtualScrollUpdate(admins: User[]): void {
    this.virtualScrollUsers = this.getAllowedAdmins(admins);
    this.virtualScrollDisabledUsers = this.getDisallowedAdmins(admins);
    this.cdRef.detectChanges();
  }

  public clearOutletAndNavigateToTeamMembers(): void {
    this.router.navigate([{ outlets: { 'job-editor': null, primary: ['settings', 'members'] } }], {
      queryParams: { invite: 'true' },
    });
  }

  private loadInvitations(): void {
    if (!this.canManageRecruiters) return;

    this.store.dispatch(
      InvitationActions.fetchPaginatedCollection({
        payload: { params: { page: 1, limit: 1000 }, scope: 'invitations' },
      }),
    );

    this.store
      .select(
        InvitationSelectors.entitiesWithPagination({
          scope: 'invitations',
        }),
      )
      .pipe(
        filter(({ rows, meta }) => !!rows && !!meta),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe((data: { rows: Invitation[] }) => {
        this.invitedEmails = data.rows.map(invite => invite.email.toLowerCase());
        this.cdRef.detectChanges();
      });
  }

  private getAdminsCount(admins: User[], assignmentIds: number[]): number {
    return admins.filter(
      admin =>
        assignmentIds.includes(admin.id) ||
        admin.roleAbilities.includes(this.requiredAbilityForGroupedAdmins),
    ).length;
  }

  private filterAndGroupAdmins(admins: User[], query: string = this.searchQuery): RoleWithAdmins[] {
    const groupedAdmins = admins
      .filter(admin => admin.roleAbilities.includes(this.requiredAbilityForGroupedAdmins))
      .reduce(
        (groups: { [key: number]: User[] }, admin) => ({
          ...groups,
          ...{ [admin.roleId]: [...(groups[admin.roleId] || []), admin] },
        }),
        {},
      );

    return Object.entries(groupedAdmins).reduce((acc, [roleId, adminGroup]: [string, User[]]) => {
      const filteredAdmins = adminGroup.filter(admin => this.adminNameContains(admin, query));
      if (filteredAdmins.length) {
        acc.push(<never>{
          id: roleId,
          name: adminGroup[0].roleName,
          count: adminGroup.length,
          admins: filteredAdmins,
          expanded: !!filteredAdmins.length && query !== '',
        });
      }
      return acc;
    }, []);
  }

  private adminNameContains({ firstName, lastName }: User, query: string): boolean {
    return includesWithTrim(`${firstName} ${lastName}`, query || '');
  }

  private getSelectedCount(): number {
    return this.assignmentsControl.value?.length;
  }

  private getAllowedAdmins(admins: User[]): User[] {
    if (this.context === 'requisition') {
      return admins.filter(
        admin =>
          admin.roleAbilities.includes(Ability.ViewRequisitions) ||
          this.assignments.some(assignment => assignment.adminId === admin.id),
      );
    }

    if (this.context === 'job') {
      return admins.filter(
        admin =>
          admin.roleAbilities.includes(Ability.ViewJobs) ||
          this.assignments.some(assignment => assignment.adminId === admin.id),
      );
    }

    return admins;
  }

  private getDisallowedAdmins(admins: User[]): User[] {
    if (this.context === 'job') {
      return admins.filter(
        admin =>
          !this.assignments.some(assignment => assignment.adminId === admin.id) &&
          !admin.roleAbilities.includes(Ability.ViewJobs),
      );
    }

    return [];
  }
}
